import {
  createClient,
  type AuthChangeEvent,
  type Session,
  type SupabaseClient,
  type User,
} from '@supabase/supabase-js';

/**
 * Cliente de Supabase del frontend.
 *
 * Manejo defensivo en **tres capas**:
 *  1. **Build-time:** si las variables de entorno están vacías o son los
 *     placeholders (`your-project`, `your_anon_key`, etc.) usamos el mock
 *     desde el primer momento.
 *  2. **Runtime probe:** si Supabase está configurado pero el proyecto real
 *     falla (DNS, 4xx/5xx, CORS), `useSupabaseAuth` lo detecta y llama a
 *     `setSupabaseMode('mock')` para conmutar al mock en caliente.
 *  3. **API surface:** el mock implementa los mismos métodos que la app
 *     consume (`auth.getSession`, `onAuthStateChange`, `signInWithPassword`,
 *     `signUp`, `signInAnonymously`, `signOut`) y persiste la sesión en
 *     `localStorage`. Los callers nunca saben si hablan con Supabase real
 *     o con el mock.
 *
 * El export `supabase` es un `Proxy` que delega en el cliente activo
 * (real ↔ mock). Esto permite cambiar de modo sin tener que re-instanciar
 * todos los hooks.
 */

// ─────────────────────────────────────────────
//   Detección de placeholder / variables vacías
// ─────────────────────────────────────────────
const PLACEHOLDER_URL_PATTERNS = [
  'your-project',
  'your-project.supabase.co',
  'your_project',
  'example.supabase.co',
  'placeholder.supabase.co',
  'change-me',
  'changeme',
];

const PLACEHOLDER_KEY_PATTERNS = [
  'your_anon_key',
  'your-anon-key',
  'your-anon-key-here',
  'changeme',
  'change-me',
  'placeholder',
];

const isPlaceholder = (value: string | undefined, patterns: string[]): boolean => {
  if (!value) return true;
  const normalized = value.trim().toLowerCase();
  if (normalized === '') return true;
  return patterns.some((p) => normalized.includes(p));
};

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

export const isSupabaseConfiguredByEnv: boolean =
  !!supabaseUrl &&
  !!supabaseAnonKey &&
  !isPlaceholder(supabaseUrl, PLACEHOLDER_URL_PATTERNS) &&
  !isPlaceholder(supabaseAnonKey, PLACEHOLDER_KEY_PATTERNS);

if (!isSupabaseConfiguredByEnv) {
  // Aviso no bloqueante: ayuda a entender por qué la app está en modo mock.
  // eslint-disable-next-line no-console
  console.warn(
    '[supabase] Variables de entorno no configuradas (o son placeholders). ' +
      'Se activa el cliente MOCK local. Define VITE_SUPABASE_URL y ' +
      'VITE_SUPABASE_ANON_KEY con valores reales en tu .env para usar Supabase real.'
  );
}

// ─────────────────────────────────────────────
//   Retorno desde un enlace de correo (confirmación / recuperación)
// ─────────────────────────────────────────────

/**
 * Lo que traía la URL al cargar la app tras pulsar un enlace del correo
 * (flujo implícito: `#access_token=…&type=signup|recovery` o
 * `#error_code=otp_expired…`). Se lee ANTES de crear el cliente, porque
 * Supabase limpia el hash al procesar la sesión.
 */
export interface AuthRedirectInfo {
  type: string | null;
  errorCode: string | null;
  errorDescription: string | null;
}

const readAuthRedirect = (): AuthRedirectInfo => {
  if (typeof window === 'undefined') return { type: null, errorCode: null, errorDescription: null };
  const params = new URLSearchParams(window.location.hash.replace(/^#/, ''));
  const query = new URLSearchParams(window.location.search);
  return {
    type: params.get('type'),
    errorCode: params.get('error_code') ?? query.get('error_code'),
    errorDescription: params.get('error_description') ?? query.get('error_description'),
  };
};

export const initialAuthRedirect: AuthRedirectInfo = readAuthRedirect();

/**
 * Origen público de la web, del que salen los enlaces de los correos: `VITE_PUBLIC_URL` si se
 * define (p. ej. `https://mryj-sportsarena.codecraft.do`, o en la app Android, donde el origen es
 * `https://localhost`), y si no el origen actual (producción o localhost en desarrollo).
 * Sin URLs fijas: el dominio se cambia solo con variables de entorno (ver docs/DOMINIO.md).
 */
export const publicOrigin = (): string => {
  const fromEnv = (import.meta.env.VITE_PUBLIC_URL ?? '').trim().replace(/\/+$/, '');
  if (fromEnv) return fromEnv;
  return typeof window !== 'undefined' ? window.location.origin : '';
};

/** URL a la que vuelven los enlaces de correo. */
export const authRedirectUrl = (path: '/login' | '/reset'): string => `${publicOrigin()}${path}`;

// ─────────────────────────────────────────────
//   Implementación del mock
// ─────────────────────────────────────────────
const MOCK_STORAGE_KEY = 'sportsarena.mockauth.session';

const readMockSession = (): Session | null => {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(MOCK_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as {
      access_token: string;
      user: { id: string; email?: string; user_metadata?: Record<string, unknown> };
      expires_at: number;
    };
    if (!parsed?.user?.id) return null;
    // Reconstruimos la Session con la forma que Supabase espera.
    const session: Session = {
      access_token: parsed.access_token,
      token_type: 'bearer',
      refresh_token: '',
      expires_in: Math.max(0, Math.floor(parsed.expires_at - Date.now() / 1000)),
      expires_at: parsed.expires_at,
      user: {
        id: parsed.user.id,
        email: parsed.user.email,
        user_metadata: parsed.user.user_metadata ?? {},
        app_metadata: {},
        aud: 'authenticated',
        created_at: new Date().toISOString(),
      } as User,
    };
    return session;
  } catch {
    return null;
  }
};

const writeMockSession = (session: Session | null): void => {
  if (typeof window === 'undefined') return;
  try {
    if (!session) {
      window.localStorage.removeItem(MOCK_STORAGE_KEY);
    } else {
      // Guardamos solo los campos necesarios; readMockSession los rehidrata.
      window.localStorage.setItem(
        MOCK_STORAGE_KEY,
        JSON.stringify({
          access_token: session.access_token,
          user: {
            id: session.user.id,
            email: session.user.email,
            user_metadata: session.user.user_metadata,
          },
          expires_at: session.expires_at ?? Math.floor(Date.now() / 1000) + 60 * 60 * 24 * 7,
        })
      );
    }
  } catch {
    /* storage no disponible (modo privado, cuota llena) → se ignora */
  }
};

const generateId = (): string => {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }
  return `mock_${Math.random().toString(36).slice(2)}_${Date.now().toString(36)}`;
};

const buildSession = (user: User): Session => ({
  access_token: `mock-token-${user.id}`,
  token_type: 'bearer',
  refresh_token: '',
  // 7 días
  expires_in: 60 * 60 * 24 * 7,
  expires_at: Math.floor(Date.now() / 1000) + 60 * 60 * 24 * 7,
  user,
});

/**
 * Mock: la misma cuenta (correo) tiene siempre el mismo id y sus metadatos se
 * conservan entre sesiones, igual que en Supabase (`auth.updateUser({ data })`
 * fusiona y un valor null borra la clave).
 */
const MOCK_USERS_KEY = 'sportsarena.mockauth.users';
const mockAccountId = (email: string): string => {
  let h = 2166136261;
  for (const ch of email.trim().toLowerCase()) h = Math.imul(h ^ ch.charCodeAt(0), 16777619);
  return `mock_${(h >>> 0).toString(36)}`;
};
const readMockUsers = (): Record<string, Record<string, unknown>> => {
  try {
    return JSON.parse(window.localStorage.getItem(MOCK_USERS_KEY) ?? '{}') as Record<string, Record<string, unknown>>;
  } catch {
    return {};
  }
};
const mockMetadata = (id: string, base: Record<string, unknown>): Record<string, unknown> => ({ ...base, ...(readMockUsers()[id] ?? {}) });
const saveMockMetadata = (id: string, patchData: Record<string, unknown>): Record<string, unknown> => {
  const users = readMockUsers();
  const next = { ...(users[id] ?? {}) };
  for (const [k, v] of Object.entries(patchData)) {
    if (v === null) delete next[k];
    else next[k] = v;
  }
  users[id] = next;
  try {
    window.localStorage.setItem(MOCK_USERS_KEY, JSON.stringify(users));
  } catch {
    /* sin almacenamiento */
  }
  return next;
};

const buildUser = (id: string, email?: string, metadata?: Record<string, unknown>): User =>
  ({
    id,
    email,
    user_metadata: metadata ?? {},
    app_metadata: {},
    aud: 'authenticated',
    created_at: new Date().toISOString(),
  }) as User;

const createMockClient = (): SupabaseClient => {
  const listeners = new Set<(event: AuthChangeEvent, session: Session | null) => void>();

  const emit = (event: AuthChangeEvent, session: Session | null) => {
    listeners.forEach((cb) => {
      try {
        cb(event, session);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('[supabase mock] listener error:', err);
      }
    });
  };

  const mockAuth = {
    getSession: async () => {
      const session = readMockSession();
      return { data: { session }, error: null };
    },
    onAuthStateChange: (cb: (event: AuthChangeEvent, session: Session | null) => void) => {
      listeners.add(cb);
      const subscription = {
        id: 'mock-subscription',
        callback: cb,
        unsubscribe: () => {
          listeners.delete(cb);
          return Promise.resolve('ok' as const);
        },
      };
      return { data: { subscription } };
    },
    signInWithPassword: async ({ email, password }: { email: string; password: string }) => {
      if (!email || !password) {
        return {
          data: { user: null, session: null },
          error: { name: 'AuthError', message: 'Email y contraseña son obligatorios.' },
        };
      }
      if (password.length < 4) {
        return {
          data: { user: null, session: null },
          error: { name: 'AuthError', message: 'La contraseña es demasiado corta.' },
        };
      }
      const id = mockAccountId(email);
      const user = buildUser(id, email, mockMetadata(id, { username: email.split('@')[0] }));
      const session = buildSession(user);
      writeMockSession(session);
      emit('SIGNED_IN', session);
      return { data: { user, session }, error: null };
    },
    signUp: async ({
      email,
      password,
      options,
    }: {
      email: string;
      password: string;
      options?: { data?: Record<string, unknown> };
    }) => {
      if (!email || !password) {
        return {
          data: { user: null, session: null },
          error: { name: 'AuthError', message: 'Email y contraseña son obligatorios.' },
        };
      }
      if (password.length < 4) {
        return {
          data: { user: null, session: null },
          error: { name: 'AuthError', message: 'La contraseña es demasiado corta.' },
        };
      }
      const id = mockAccountId(email);
      const user = buildUser(id, email, mockMetadata(id, options?.data ?? {}));
      const session = buildSession(user);
      writeMockSession(session);
      emit('SIGNED_IN', session);
      return { data: { user, session }, error: null };
    },
    signInAnonymously: async () => {
      const user = buildUser(
        `anon_${generateId()}`,
        undefined,
        { username: 'Invitado' }
      );
      const session = buildSession(user);
      writeMockSession(session);
      emit('SIGNED_IN', session);
      return { data: { user, session }, error: null };
    },
    signOut: async () => {
      writeMockSession(null);
      emit('SIGNED_OUT', null);
      return { error: null };
    },
    // Sin servidor de correo en el mock: se aceptan y no hacen nada.
    resend: async () => ({ data: {}, error: null }),
    resetPasswordForEmail: async () => ({ data: {}, error: null }),
    updateUser: async (attrs?: { data?: Record<string, unknown> }) => {
      const session = readMockSession();
      if (!session) return { data: { user: null }, error: { name: 'AuthError', message: 'Auth session missing!' } };
      if (attrs?.data) {
        const meta = saveMockMetadata(session.user.id, attrs.data);
        session.user = { ...session.user, user_metadata: { ...session.user.user_metadata, ...meta } } as User;
        for (const [k, v] of Object.entries(attrs.data)) if (v === null) delete (session.user.user_metadata as Record<string, unknown>)[k];
        writeMockSession(session);
        emit('USER_UPDATED', session);
      }
      return { data: { user: session.user }, error: null };
    },
    getUser: async () => {
      const session = readMockSession();
      return { data: { user: session?.user ?? null }, error: null };
    },
  };

  return {
    auth: mockAuth,
  } as unknown as SupabaseClient;
};

const createRealClient = (): SupabaseClient =>
  createClient(supabaseUrl!, supabaseAnonKey!, {
    auth: {
      persistSession: true,
      autoRefreshToken: true,
      detectSessionInUrl: true,
      storageKey: 'sportsarena.auth',
    },
  });

// ─────────────────────────────────────────────
//   Switch de modo + Proxy
// ─────────────────────────────────────────────

type SupabaseMode = 'real' | 'mock';

let currentMode: SupabaseMode = isSupabaseConfiguredByEnv ? 'real' : 'mock';
let currentClient: SupabaseClient =
  currentMode === 'real' ? createRealClient() : createMockClient();

/** Suscriptores que quieren saber cuándo cambia el modo. */
const modeListeners = new Set<(mode: SupabaseMode) => void>();

/**
 * Conmuta entre cliente real y mock en caliente. El `Proxy` exportado
 * apunta siempre a `currentClient`, así que cambiarlo afecta a todos los
 * callers sin re-importar.
 */
export const setSupabaseMode = (mode: SupabaseMode, reason?: string): void => {
  if (mode === currentMode) return;
  if (mode === 'real' && !isSupabaseConfiguredByEnv) return; // no hay URL real
  currentMode = mode;
  currentClient = mode === 'real' ? createRealClient() : createMockClient();
  modeListeners.forEach((cb) => {
    try {
      cb(mode);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[supabase] mode listener error:', err);
    }
  });
  // eslint-disable-next-line no-console
  console.warn(
    `[supabase] Modo conmutado a "${mode}"${reason ? ` (${reason})` : ''}.`
  );
};

/** Suscríbete a cambios de modo. Devuelve función de cleanup. */
export const onSupabaseModeChange = (cb: (mode: SupabaseMode) => void): (() => void) => {
  modeListeners.add(cb);
  return () => {
    modeListeners.delete(cb);
  };
};

/** Modo actual (real | mock). */
export const getSupabaseMode = (): SupabaseMode => currentMode;

/**
 * Cliente público que la app consume. Es un Proxy que delega en el cliente
 * activo (real o mock). Si en runtime se detecta que Supabase real falla,
 * `setSupabaseMode('mock')` cambia el destino y todas las llamadas
 * posteriores irán al mock.
 */
export const supabase: SupabaseClient = new Proxy({} as SupabaseClient, {
  get(_target, prop: string | symbol) {
    // Atajos de tipos para evitar diferencias entre real y mock.
    const value = (currentClient as unknown as Record<string | symbol, unknown>)[prop];
    return typeof value === 'function' ? (value as (...a: unknown[]) => unknown).bind(currentClient) : value;
  },
  has(_target, prop: string | symbol) {
    return prop in (currentClient as unknown as object);
  },
}) as SupabaseClient;

/** `true` si el cliente activo es el mock (config o fallback runtime). */
export const isSupabaseMock: boolean = currentMode === 'mock';

/** Re-exports de tipos útiles para hooks / componentes. */
export type { Session, AuthChangeEvent };
